import * as Yup from 'yup';
import { FACILITY_TYPES } from '../../../utils/constants/facilityTypes';
import { TECHNICAL_CONDITIONS } from '../../../utils/constants/technicalConditions';
import { SHUTDOWN_STATUSES } from '../../../utils/constants/shutdownStatuses';
import { LIMITS_COMPLIANCE } from '../../../utils/constants/limitsCompliance';

export const formValues = {
    date: new Date(),
    responsiblePersons: [],
    wasteReceived: [],
    wasteProcessed: [],
    wasteRejected: [],
    solidLiquidWasteHandling: '',
    samples: [],
    technicalCondition: TECHNICAL_CONDITIONS.NO_DEFECT,
    malfunctions: [],
    operatingHours: '',
    shutdown: { status: SHUTDOWN_STATUSES.NONE, reason: '' },
    maintenance: [],
    authorityInspections: [],
    limitsCompliance: LIMITS_COMPLIANCE.COMPLIANT,
    otherActivities: '',
    landfillDetails: {
        hazardousWastePlacement: '',
        compaction: '',
        layerThicknessAndCovering: '',
        monitoringWellSamples: [],
        inertMaterialsUsed: [],
        waterPumping: ''
    },
    mercuryReceived: [],
    mercuryDispatched: []
};

const wasteLineShape = {
    wasteCode: Yup.mixed().nullable().required('Kód odpadu je povinný.'),
    amount: Yup.number().typeError('Množstvo musí byť číslo.').min(0, 'Množstvo nemôže byť záporné.').required('Množstvo je povinné.'),
};

export const buildValidation = (facilityType) => Yup.object({
    date: Yup.date().typeError('Dátum je povinný.').required('Dátum je povinný.'),
    technicalCondition: Yup.string().oneOf([...Object.values(TECHNICAL_CONDITIONS), ''], 'Neplatný technický stav zariadenia.'),
    limitsCompliance: Yup.string().oneOf([...Object.values(LIMITS_COMPLIANCE), ''], 'Neplatný stav dodržiavania limitov.'),
    shutdown: Yup.object({
        status: Yup.string().oneOf(Object.values(SHUTDOWN_STATUSES), 'Neplatný stav odstavenia zariadenia.'),
        reason: Yup.string().when('status', {
            is: (status) => status && status !== SHUTDOWN_STATUSES.NONE,
            then: (schema) => schema.required('Čas a dôvod odstavenia je povinný.'),
        }),
    }),
    responsiblePersons: Yup.array()
        .of(Yup.string())
        .min(1, 'Zadajte aspoň jedného zodpovedného zamestnanca.'),
    wasteReceived: Yup.array().of(Yup.object({
        ...wasteLineShape,
        loadingCode: Yup.string().required('Kód nakladania je povinný.'),
    })),
    wasteProcessed: Yup.array().of(Yup.object({
        ...wasteLineShape,
        loadingCode: Yup.string().required('Kód nakladania je povinný.'),
    })),
    wasteRejected: Yup.array().of(Yup.object({
        wasteCode: Yup.mixed().nullable(),
        amount: Yup.number().typeError('Množstvo musí byť číslo.').min(0, 'Množstvo nemôže byť záporné.'),
        producerOrHolder: Yup.string(),
        reason: Yup.string().required('Zdôvodnenie neprevzatia je povinné.'),
    })),
    samples: Yup.array().of(Yup.object({
        takenAt: Yup.date().typeError('Dátum odberu je povinný.').required('Dátum odberu je povinný.'),
        description: Yup.string(),
        resultsSummary: Yup.string(),
    })),
    malfunctions: Yup.array().of(Yup.object({
        description: Yup.string().required('Popis poruchy je povinný.'),
        resolution: Yup.string(),
    })),
    maintenance: Yup.array().of(Yup.object({
        description: Yup.string().required('Popis údržby/opravy je povinný.'),
    })),
    authorityInspections: Yup.array().of(Yup.object({
        authority: Yup.string().required('Kontrolný orgán je povinný.'),
        from: Yup.date().nullable(),
        to: Yup.date().nullable(),
        notes: Yup.string(),
    })),
    landfillDetails: facilityType === FACILITY_TYPES.SKLADKA
        ? Yup.object({
            hazardousWastePlacement: Yup.string().required('Umiestnenie nebezpečného odpadu je povinné.'),
            compaction: Yup.string().required('Údaj o zhutnení je povinný.'),
            layerThicknessAndCovering: Yup.string().required('Hrúbka vrstvy a prekrytie je povinné.'),
            monitoringWellSamples: Yup.array().of(Yup.object({
                wellId: Yup.string().required('Označenie vrtu je povinné.'),
                takenAt: Yup.date().typeError('Dátum odberu je povinný.').required('Dátum odberu je povinný.'),
                resultsSummary: Yup.string(),
            })),
            inertMaterialsUsed: Yup.array().of(Yup.object({
                materialName: Yup.string().required('Názov materiálu je povinný.'),
                amount: Yup.number().typeError('Množstvo musí byť číslo.').min(0, 'Množstvo nemôže byť záporné.'),
            })),
            waterPumping: Yup.string(),
        })
        : Yup.object().notRequired(),
    mercuryReceived: Yup.array().of(Yup.object({
        amount: Yup.number().typeError('Množstvo musí byť číslo.').min(0, 'Množstvo nemôže byť záporné.').required('Množstvo je povinné.'),
        producerOrHolder: Yup.string().required('Pôvodca/držiteľ je povinný.'),
        containerCertificateNumber: Yup.string().required('Číslo potvrdenia o kontajneri je povinné.'),
    })),
    mercuryDispatched: Yup.array().of(Yup.object({
        amount: Yup.number().typeError('Množstvo musí byť číslo.').min(0, 'Množstvo nemôže byť záporné.').required('Množstvo je povinné.'),
        recipient: Yup.string().required('Príjemca je povinný.'),
        treatmentFacility: Yup.string(),
        permanentStorageFacility: Yup.string(),
    })),
});
